import { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Card, Row, Col, Form, Button, Badge, Alert, Spinner, InputGroup } from 'react-bootstrap';
import {
  fetchCatalogoAdminConfig, updateCatalogoConfig,
  fetchCatalogoFiltros, updateCatalogoFiltros,
  fetchCatalogoTodosProdutos, ocultarProdutoCatalogo,
  mostrarProdutoCatalogo, mostrarProdutoLoteCatalogo,
  fetchCatalogoOcultos
} from '../../services/api';
import './CatalogoAdmin.css';

const TABS = [
  { id: 'config', icon: '⚙️', label: 'Configurações' },
  { id: 'produtos', icon: '📦', label: 'Produtos' },
  { id: 'filtros', icon: '🏷️', label: 'Categorias / Fornecedores' },
  { id: 'fotos', icon: '📸', label: 'Formulário Fotos' }
];

export default function CatalogoAdmin() {
  const [tab, setTab] = useState('config');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // ── Config state ──
  const [config, setConfig] = useState({});

  // ── Produtos state ──
  const [produtos, setProdutos] = useState([]);
  const [prodSearch, setProdSearch] = useState('');
  const [prodPage, setProdPage] = useState(1);
  const [prodTotal, setProdTotal] = useState(0);
  const [prodTotalPages, setProdTotalPages] = useState(1);
  const [totalOcultos, setTotalOcultos] = useState(0);
  const [selecionados, setSelecionados] = useState(new Set());
  const [ocultos, setOcultos] = useState([]);
  const [mostrarSoOcultos, setMostrarSoOcultos] = useState(false);
  const searchTimer = useRef(null);

  // ── Filtros state ──
  const [filtrosData, setFiltrosData] = useState({
    categoriasAtivas: [], fornecedoresAtivos: [],
    todasCategorias: [], todosFornecedores: []
  });
  const [catSel, setCatSel] = useState([]);
  const [fornSel, setFornSel] = useState([]);

  // ── Mostra mensagem temporária ──
  const flash = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  // ═══════════ CONFIG ═══════════
  const carregarConfig = useCallback(async () => {
    setLoading(true);
    const r = await fetchCatalogoAdminConfig();
    if (r.success) setConfig(r.data || {});
    setLoading(false);
  }, []);

  const salvarConfig = async () => {
    setLoading(true);
    const r = await updateCatalogoConfig(config);
    if (r.success) flash('Configurações salvas!');
    else flash('Erro ao salvar', 'danger');
    setLoading(false);
  };

  // ═══════════ PRODUTOS ═══════════
  const carregarProdutos = useCallback(async (pg = 1) => {
    setLoading(true);
    const r = await fetchCatalogoTodosProdutos({ page: pg, limit: 100, search: prodSearch });
    if (r.success) {
      setProdutos(r.data || []);
      setProdTotal(r.pagination?.total || 0);
      setProdTotalPages(r.pagination?.totalPages || 1);
      setTotalOcultos(r.totalOcultos || 0);
      setProdPage(pg);
    }
    setLoading(false);
  }, [prodSearch]);

  const carregarOcultos = useCallback(async () => {
    const r = await fetchCatalogoOcultos();
    if (r.success) setOcultos(r.data || []);
  }, []);

  const handleOcultar = async (codigos) => {
    const lista = Array.isArray(codigos) ? codigos : [codigos];
    const r = await ocultarProdutoCatalogo(lista);
    if (r.success) {
      flash(`${lista.length} produto(s) oculto(s)`);
      carregarProdutos(prodPage);
      carregarOcultos();
      setSelecionados(new Set());
    }
  };

  const handleMostrar = async (codigo) => {
    const r = await mostrarProdutoCatalogo(codigo);
    if (r.success) {
      flash('Produto reexibido');
      carregarProdutos(prodPage);
      carregarOcultos();
    }
  };

  const handleMostrarLote = async (codigos) => {
    const r = await mostrarProdutoLoteCatalogo(codigos);
    if (r.success) {
      flash(`${codigos.length} produto(s) reexibido(s)`);
      carregarProdutos(prodPage);
      carregarOcultos();
      setSelecionados(new Set());
    }
  };

  const toggleSelecionado = (codigo) => {
    setSelecionados(prev => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  };

  // ═══════════ FILTROS ═══════════
  const carregarFiltros = useCallback(async () => {
    setLoading(true);
    const r = await fetchCatalogoFiltros();
    if (r.success && r.data) {
      setFiltrosData(r.data);
      setCatSel(r.data.categoriasAtivas || []);
      setFornSel(r.data.fornecedoresAtivos || []);
    }
    setLoading(false);
  }, []);

  const salvarFiltros = async () => {
    setLoading(true);
    const r = await updateCatalogoFiltros({ categoriasAtivas: catSel, fornecedoresAtivos: fornSel });
    if (r.success) flash('Filtros salvos!');
    else flash('Erro ao salvar filtros', 'danger');
    setLoading(false);
  };

  const toggleCat = (cat) => setCatSel(prev =>
    prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
  );
  const toggleForn = (forn) => setFornSel(prev =>
    prev.includes(forn) ? prev.filter(f => f !== forn) : [...prev, forn]
  );

  // ═══════════ EFFECTS ═══════════
  useEffect(() => { carregarConfig(); }, [carregarConfig]);

  useEffect(() => {
    if (tab === 'produtos') {
      carregarProdutos(1);
      carregarOcultos();
    } else if (tab === 'filtros') {
      carregarFiltros();
    }
  }, [tab]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => carregarProdutos(1), 400);
    return () => clearTimeout(searchTimer.current);
  }, [prodSearch]);

  // Produtos filtrados (se mostrar só ocultos)
  const produtosVisiveis = mostrarSoOcultos ? produtos.filter(p => p.oculto) : produtos;

  const linkFotos = `${window.location.origin}/catalogo/?modo=fotos`;

  return (
    <Container fluid className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-0 text-gray-800">🎨 Admin do Catálogo</h1>
          <p className="mb-0 text-muted">Gerencie o catálogo público de produtos</p>
        </div>
        <a href="/catalogo/" target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
          🔗 Ver catálogo público
        </a>
      </div>

      {msg && <Alert variant={msg.type} className="mb-3" dismissible onClose={() => setMsg(null)}>{msg.text}</Alert>}

      {/* Tabs */}
      <div className="cat-admin-tabs mb-4">
        {TABS.map(t => (
          <button key={t.id} className={`cat-admin-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* ═══ ABA CONFIG ═══ */}
      {tab === 'config' && (
        <Card className="shadow">
          <Card.Header className="bg-white py-3"><h6 className="m-0 fw-bold text-primary">⚙️ Configurações da Loja</h6></Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Nome da Loja</Form.Label>
                  <Form.Control value={config.nome_loja || ''} onChange={e => setConfig(c => ({ ...c, nome_loja: e.target.value }))} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">WhatsApp</Form.Label>
                  <Form.Control value={config.whatsapp || ''} onChange={e => setConfig(c => ({ ...c, whatsapp: e.target.value }))} placeholder="21979673042" />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Instagram</Form.Label>
                  <Form.Control value={config.instagram || ''} onChange={e => setConfig(c => ({ ...c, instagram: e.target.value }))} placeholder="@gringaoacoinox" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Pedido Mínimo</Form.Label>
                  <Form.Control value={config.pedido_minimo || ''} onChange={e => setConfig(c => ({ ...c, pedido_minimo: e.target.value }))} placeholder="Ex: Pedido mínimo R$ 50,00" />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">URL do Logo</Form.Label>
                  <Form.Control value={config.logo_url || ''} onChange={e => setConfig(c => ({ ...c, logo_url: e.target.value }))} placeholder="https://..." />
                </Form.Group>
                <Form.Check type="switch" className="mb-3 mt-4" label="Mostrar preços no catálogo" checked={!!config.mostrar_preco} onChange={e => setConfig(c => ({ ...c, mostrar_preco: e.target.checked }))} />
              </Col>
            </Row>
            <div className="text-end">
              <Button variant="primary" onClick={salvarConfig} disabled={loading}>
                {loading ? <Spinner size="sm" className="me-2" /> : null} 💾 Salvar
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ═══ ABA PRODUTOS ═══ */}
      {tab === 'produtos' && (
        <>
          <Row className="mb-3 g-3">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>🔍</InputGroup.Text>
                <Form.Control placeholder="Buscar por código ou nome..." value={prodSearch} onChange={e => setProdSearch(e.target.value)} />
              </InputGroup>
            </Col>
            <Col md={3} className="d-flex align-items-center gap-3">
              <Badge bg="secondary">{prodTotal} total</Badge>
              <Badge bg="danger">{totalOcultos} ocultos</Badge>
              <Form.Check type="switch" label="Só ocultos" checked={mostrarSoOcultos} onChange={e => setMostrarSoOcultos(e.target.checked)} />
            </Col>
            <Col md={5} className="d-flex justify-content-end gap-2">
              {selecionados.size > 0 && (
                <>
                  <Button variant="danger" size="sm" onClick={() => handleOcultar([...selecionados])}>
                    🚫 Ocultar {selecionados.size} selecionados
                  </Button>
                  <Button variant="success" size="sm" onClick={() => handleMostrarLote([...selecionados])}>
                    ✅ Mostrar {selecionados.size} selecionados
                  </Button>
                </>
              )}
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : (
            <Card className="shadow">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 40 }}>
                        <Form.Check type="checkbox"
                          checked={produtosVisiveis.length > 0 && selecionados.size === produtosVisiveis.length}
                          onChange={e => {
                            if (e.target.checked) setSelecionados(new Set(produtosVisiveis.map(p => p.codigo)));
                            else setSelecionados(new Set());
                          }}
                        />
                      </th>
                      <th style={{ width: 60 }}>Foto</th>
                      <th>Código</th>
                      <th>Nome</th>
                      <th>Categoria</th>
                      <th>Fornecedor</th>
                      <th>Status</th>
                      <th style={{ width: 100 }}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtosVisiveis.map(p => (
                      <tr key={p.codigo} className={p.oculto ? 'table-danger' : ''}>
                        <td><Form.Check type="checkbox" checked={selecionados.has(p.codigo)} onChange={() => toggleSelecionado(p.codigo)} /></td>
                        <td>
                          <img src={p.imagemURL} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }}
                            onError={e => { e.target.style.display = 'none'; }} />
                        </td>
                        <td className="text-nowrap"><code>{p.codigo}</code></td>
                        <td>{p.nome?.substring(0, 50)}{p.nome?.length > 50 ? '...' : ''}</td>
                        <td><Badge bg="info" className="fw-normal">{p.categoria}</Badge></td>
                        <td className="text-muted small">{p.fornecedor?.substring(0, 25)}</td>
                        <td>
                          {p.oculto
                            ? <Badge bg="danger">🚫 Oculto</Badge>
                            : <Badge bg="success">✅ Visível</Badge>
                          }
                        </td>
                        <td>
                          {p.oculto ? (
                            <Button variant="outline-success" size="sm" onClick={() => handleMostrar(p.codigo)}>Mostrar</Button>
                          ) : (
                            <Button variant="outline-danger" size="sm" onClick={() => handleOcultar(p.codigo)}>Ocultar</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {prodTotalPages > 1 && (
                <Card.Footer className="d-flex justify-content-center gap-3 py-3">
                  <Button variant="outline-secondary" size="sm" disabled={prodPage <= 1} onClick={() => carregarProdutos(prodPage - 1)}>← Anterior</Button>
                  <span className="text-muted align-self-center">Página {prodPage} de {prodTotalPages}</span>
                  <Button variant="outline-secondary" size="sm" disabled={prodPage >= prodTotalPages} onClick={() => carregarProdutos(prodPage + 1)}>Próxima →</Button>
                </Card.Footer>
              )}
            </Card>
          )}

          {/* Lista de ocultos */}
          {ocultos.length > 0 && (
            <Card className="shadow mt-4">
              <Card.Header className="bg-white py-3">
                <h6 className="m-0 fw-bold text-danger">🚫 Produtos Ocultos ({ocultos.length})</h6>
              </Card.Header>
              <Card.Body>
                <div className="d-flex flex-wrap gap-2">
                  {ocultos.map(o => (
                    <div key={o.codigo} className="cat-admin-oculto-chip">
                      <span className="fw-bold">{o.codigo}</span>
                      <span className="text-muted ms-1">{o.nome?.substring(0, 30)}</span>
                      <button className="cat-admin-oculto-remove" onClick={() => handleMostrar(o.codigo)} title="Reexibir">✕</button>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {/* ═══ ABA FILTROS ═══ */}
      {tab === 'filtros' && (
        <Row className="g-4">
          <Col md={6}>
            <Card className="shadow h-100">
              <Card.Header className="bg-white py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="m-0 fw-bold text-primary">🏷️ Categorias</h6>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => setCatSel(filtrosData.todasCategorias)}>Todas</Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setCatSel([])}>Nenhuma</Button>
                  </div>
                </div>
                <small className="text-muted">{catSel.length === 0 ? 'Todas (sem filtro)' : `${catSel.length} selecionadas`}</small>
              </Card.Header>
              <Card.Body style={{ maxHeight: 400, overflowY: 'auto' }}>
                {filtrosData.todasCategorias.map(cat => (
                  <Form.Check key={cat} type="checkbox" className="mb-2"
                    label={cat} checked={catSel.includes(cat)} onChange={() => toggleCat(cat)} />
                ))}
                {filtrosData.todasCategorias.length === 0 && <p className="text-muted">Nenhuma categoria encontrada</p>}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="shadow h-100">
              <Card.Header className="bg-white py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="m-0 fw-bold text-primary">🏭 Fornecedores</h6>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => setFornSel(filtrosData.todosFornecedores)}>Todos</Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setFornSel([])}>Nenhum</Button>
                  </div>
                </div>
                <small className="text-muted">{fornSel.length === 0 ? 'Todos (sem filtro)' : `${fornSel.length} selecionados`}</small>
              </Card.Header>
              <Card.Body style={{ maxHeight: 400, overflowY: 'auto' }}>
                {filtrosData.todosFornecedores.map(forn => (
                  <Form.Check key={forn} type="checkbox" className="mb-2"
                    label={forn} checked={fornSel.includes(forn)} onChange={() => toggleForn(forn)} />
                ))}
                {filtrosData.todosFornecedores.length === 0 && <p className="text-muted">Nenhum fornecedor encontrado</p>}
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12}>
            <div className="text-end">
              <Button variant="primary" size="lg" onClick={salvarFiltros} disabled={loading}>
                {loading ? <Spinner size="sm" className="me-2" /> : null} 💾 Salvar Filtros
              </Button>
            </div>
          </Col>
        </Row>
      )}

      {/* ═══ ABA FORMULÁRIO FOTOS ═══ */}
      {tab === 'fotos' && (
        <Card className="shadow">
          <Card.Header className="bg-white py-3"><h6 className="m-0 fw-bold text-primary">📸 Formulário Visual — Somente Fotos</h6></Card.Header>
          <Card.Body>
            <Alert variant="info">
              <strong>Como funciona:</strong> O link abaixo mostra o catálogo apenas com as fotos dos produtos (sem preço, sem estoque, sem carrinho).
              Ideal para compartilhar em redes sociais ou enviar para clientes que querem ver apenas as fotos.
            </Alert>
            <InputGroup className="mb-4">
              <Form.Control readOnly value={linkFotos} className="bg-light" />
              <Button variant="primary" onClick={() => {
                navigator.clipboard.writeText(linkFotos);
                flash('Link copiado!');
              }}>📋 Copiar link</Button>
            </InputGroup>
            <div className="text-center">
              <a href={linkFotos} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-lg">
                🔗 Abrir formulário de fotos
              </a>
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
